
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { ApplicationState } from "../store/application-store";
import { ProductPreviewComponent } from "@feature/preview-mode/containers/product-preview/product-preview.component";

import { Store } from "@ngrx/store";
import { combineLatest, Observable, of, pipe } from "rxjs";
import { filter, map, switchMap } from "rxjs/operators";
import * as authActions from "../auth/actions/auth";
import { ActivatedRoute, Router } from "@angular/router";
import { Menu } from "primeng/menu";
import { MenuItem } from "primeng/api";

import { MojoUrlProvider } from "@mojo/services/mojo-url-provider";

import * as widgetGalleryActions from "../widget-gallery/actions/widget-gallery.actions";

import { BrandService } from "../brand/services/brand.service";
import { DashboardService } from "../dashboards/services/dashboard.service";
import { distinctUntilChanged, takeWhile } from "rxjs/operators";

import { OidcAuthService } from "../auth/services/oidc-auth.service";
import { UserProfile } from "../auth/models/user-profile";
import { UserSettingsService } from "../user-settings/services/user-settings.service";
import { UserSettings } from "../user-settings/models/user-settings";

import { PreviewModeService } from "../preview-mode/services/preview-mode.service";
import * as userSettingsActions from "../user-settings/actions/user-settings";
import { GeneralInformation } from "../admin/models/system-configuration-model";
import { SystemConfigurationService } from "../admin/services/system-configuration.service";
import { ToastrService } from "ngx-toastr";

import { ProductNavbarProviderService } from "@feature/navbar/services/product-navbar-provider.service";
import { ProductNavbar } from "@feature/navbar/services/product-navbar";
import { AddToHomeScreenService } from "@feature/_app_modules/mojo-for-tenants/add-to-homescreen-service/add-to-homescreen.service";
import {MojoWellKnownNames} from "@feature/common";

@Component({
  selector: "mojo-navbar",
  templateUrl: "./navbar.component.html",
})
export class NavbarComponent implements OnInit, OnDestroy {
  isAlive: boolean = true;

  @ViewChild("productPreviewComponent", { static: true })
  productPreviewComponent: ProductPreviewComponent;

  @ViewChild("systemmenu", { static: true })
  systemMenu: Menu;

  @ViewChild("usermenu", { static: true })
  userMenu: Menu;

  items: MenuItem[];

  userName$: Observable<string>;
  appName$: Observable<string>;
  systemMenuItems: Observable<MenuItem[]>;
  userMenuItems: Observable<MenuItem[]>;
  homepageUrl$: Observable<string>;
  showPreviewForProduct: Observable<string>;
  displayContactUs: Observable<boolean>;

  displayAboutDialog: boolean = false;
  systemMenuStyleClass: string = "system-menu";
  userMenuStyleClass: string = "user-menu";

  isWorksEnabled: boolean;
  inPreviewMode: boolean;
  isInMyDashboardMode: boolean;
  showMojoNavHeader: boolean;

  daysLeftBeforePasswordExpires = -1;
  currentProduct: string;

  private showLogoutMenu: boolean = true;

  private productNavbar: ProductNavbar;

  constructor(
    private readonly mojoUrls: MojoUrlProvider,
    private readonly productNavbarProvider: ProductNavbarProviderService,
    private readonly store: Store<ApplicationState>,
    private readonly router: Router,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly route: ActivatedRoute,
    private readonly brandService: BrandService,
    private readonly dashboardService: DashboardService,
    private readonly authService: OidcAuthService,
    private readonly userSettingsService: UserSettingsService,
    private readonly previewModeService: PreviewModeService,
    private readonly systemConfigurationService: SystemConfigurationService,
    private readonly toastService: ToastrService,
    private readonly addToHomeScreenService: AddToHomeScreenService
  ) {
    const brandChanged = this.brandService.isMojoBrand().pipe(
      distinctUntilChanged(),
      takeWhile(() => this.isAlive),
    );
    const userSettingsChanged = this.userSettingsService.getUserSettings().pipe(
      takeWhile(() => this.isAlive),
    );
    const userProfileChanged = this.authService.getUserProfile().pipe(
      takeWhile(() => this.isAlive),
      distinctUntilChanged(),
      filter((profile) => profile != null),
    );
    const config = combineLatest(
      brandChanged,
      userSettingsChanged,
      userProfileChanged,
    );

    config.subscribe((all: [boolean, UserSettings, UserProfile]) => {
      const [isMojoBrand, settings, profile] = all;

      this.showLogoutMenu = !profile.authenticatedViaDesktopApp;

      this.showMojoNavHeader = isMojoBrand;
      this.inPreviewMode = settings.IsInPreviewMode;
      this.isInMyDashboardMode = settings.IsInMyDashboardMode;
      this.currentProduct = settings.Product;
      this.isWorksEnabled = settings.EnabledAddOns.includes(MojoWellKnownNames.AddOnCodes.WS007_Works);

      if (profile.daysLeftBeforePasswordExpires) {
        this.daysLeftBeforePasswordExpires =
          profile.daysLeftBeforePasswordExpires;
      }

      this.productNavbar = productNavbarProvider.getProductNavbar(
        this.currentProduct,
      );
      this.productNavbar.initialise();

      this.userName$ = this.productNavbar.userName;
      this.homepageUrl$ = this.productNavbar.homepageUrl;
      this.showPreviewForProduct = this.productNavbar.requestPreviewMode;

      this.systemMenuItems = this.productNavbar.systemMenuItems.pipe(
        map((productSpecificItems) => {
          const items: Array<MenuItem> = [].concat(productSpecificItems);
          items.push({
            id: "systemMenuAbout",
            label: "About",
            command: (() => {
              this.displayAboutDialog = true;
            }),
          });

          return [
            {
              label: "Menu",
              items: items,
            },
          ];
        }),
      );

      this.userMenuItems = combineLatest([
        this.productNavbar.userMenuItems,
        this.addToHomeScreenService.deferredPrompt,
      ]).pipe(map(([productSpecificItems, deferredPrompt]) => {
        const items: Array<MenuItem> = [];

        if (this.inPreviewMode) {
          items.push({
            label: "Exit Preview Mode",
            command: (event) => this.exitPreviewModeClick(),
          });
        } else {
          items.push(...productSpecificItems);

          if (this.canAddWidgets()) {
            items.push({
              label: "Widget Selection",
              command: () => {
                this.showWidgetGallery();
              },
            });
          }

          if (deferredPrompt) {
            items.push({
              label: "Add to Home Screen",
              command: (() => {
                this.addToHomeScreen();
              }),
            });
          }
          if (this.isInMyDashboardMode) {
            items.push({
              label: "Preferences",
              command: (() => {
                this.userPreferences();
              }),
            });
          }

          items.push({
            label: "Change Password",
            command: (() => {
              this.changePassword();
            }),
          });

          if (this.showLogoutMenu) {
            items.push({
              label: "Log Out",
              command: (() => {
                this.logout();
              }),
            });
          }
        }

        return [{
          label: "User Menu",
          items: items,
        }];
      }));
    });

    // should this be product title service?
    this.appName$ = this.userSettingsService.getCurrentProduct().pipe(
      filter((product: string) => {
        return product != undefined;
      }),
      switchMap((product: string) => {
        return this.systemConfigurationService.getGeneralInformation(product);
      }),
      map((generalInfo: GeneralInformation) => generalInfo.applicationName),
    );
  }

  userPreferences() : void {
    this.router.navigate(["/user-preferences"], { queryParams: { returnUrl: window.location.pathname }});
  }

  changePassword(): void {
    this.authService.changePassword(window.location.href);
  }

  addToHomeScreen() {
    this.addToHomeScreenService.promptFunction();
  }

  logout() {
    this.store.dispatch(new authActions.Logout(this.isInMyDashboardMode));
  }

  private showWidgetGallery(): void {
    this.store.dispatch(new widgetGalleryActions.Open());
  }

  private canAddWidgets(): boolean {
    return this.dashboardService.canAddWidgets(this.route);
  }

  ngOnInit() {
    this.items = [{
      label: "Options",
      items: [{
        label: "Update",
        icon: "pi pi-refresh",
      }, {
        label: "Delete",
        icon: "pi pi-times",
      }],
    }, {
      label: "Navigate",
      items: [{
        label: "Angular Website",
        icon: "pi pi-external-link",
        url: "http://angular.io",
      }, {
        label: "Router",
        icon: "pi pi-upload",
      }],
    }];
  }


  exitPreviewModeClick() {
    this.previewModeService.exitPreviewMode().pipe(
      takeWhile(() => this.isAlive),
    ).subscribe((result) => {
      if (result) {
        this.store.dispatch(new userSettingsActions.Load());
        this.router.navigated = false;
        const url = "/main";
        this.router.navigate([url]);
      }
    });
  }

  passwordExpiryWarningClick() {
    if (this.daysLeftBeforePasswordExpires === 0) {
      const msg =
        `Your password expires today. Click here to change your password.`;
      const toast = this.toastService.error(msg, "Warning", {
        closeButton: true,
      });
      toast.onTap.subscribe(() => this.changePassword());
    } else {
      const msg =
        `Your password will expire in ${this.daysLeftBeforePasswordExpires} days(s). Click here to change your password.`;
      const toast = this.toastService.warning(msg, "Warning", {
        closeButton: true,
      });
      toast.onTap.subscribe(() => this.changePassword());
    }
  }

  showPasswordExpiryWarning(): boolean {
    return this.daysLeftBeforePasswordExpires >= 0;
  }

  getActivityListUrl() {
    return this.mojoUrls.myActivities();
  }

  getNewActivityUrl() {
    return this.mojoUrls.newActivity();
  }

  getWorksUrl() {
    return this.mojoUrls.works();
  }

  ngOnDestroy() {
    this.isAlive = false;
  }

  get showCircleIcons(): boolean {
    return this.isInMyDashboardMode && this.showMojoNavHeader && !this.inPreviewMode;
  }
}
